// New file for time-related utilities

// Get user's selected timezone preference, fallback to browser timezone
export const getUserTimeZone = (user) => {
    return user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Legacy export for backward compatibility (will be deprecated)
export const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const parseGameDateTime = (dateString) => {
  try {
    if (!dateString) {
      console.warn('Missing date string:', dateString);
      return null;
    }

    let date;

    // Handle SQL datetime format (YYYY-MM-DD HH:mm:ss)
    if (dateString.includes(' ') && !dateString.includes('T')) {
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute, second] = timePart.split(':');

      // Create date assuming UTC for SQL timestamps
      date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    } else {
      // Handle ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.error('Invalid date created from:', dateString);
      return null;
    }

    return date;
  } catch (error) {
    console.error('Error parsing date time:', error, { dateString });
    return null;
  }
};

export const formatGameDateTime = (isoString, user = null) => {
  try {
    const dateTime = parseGameDateTime(isoString);
    if (!dateTime) {
      return {
        groupDate: 'Invalid Date',
        displayDate: 'Invalid Date',
        displayTime: 'Invalid Time',
        headerDate: 'Invalid Date'
      };
    }

    const timeZone = getUserTimeZone(user);

    // Format for grouping (YYYY-MM-DD in user timezone)
    const groupDate = dateTime.toLocaleDateString('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Format for display in GameCard (e.g., "11/1")
    const displayDate = dateTime.toLocaleDateString('en-US', {
      timeZone: timeZone,
      month: 'numeric',
      day: 'numeric'
    });

    // Format for section headers (e.g., "Thu, 11/1")
    const headerDate = dateTime.toLocaleDateString('en-US', {
      timeZone: timeZone,
      weekday: 'short',
      month: 'numeric',
      day: 'numeric'
    });

    // Format time (e.g., "11:00 AM")
    const displayTime = dateTime.toLocaleTimeString('en-US', {
      timeZone: timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return {
      groupDate,
      displayDate,
      displayTime,
      headerDate
    };
  } catch (error) {
    console.error('Error formatting date time:', error, { isoString });
    return {
      groupDate: 'Invalid Date',
      displayDate: 'Invalid Date',
      displayTime: 'Invalid Time',
      headerDate: 'Invalid Date'
    };
  }
};

// Format date and time in MM/DD/YY HH:MM AM/PM format
export const formatDateTime = (isoString, user = null) => {
  try {
    const dateTime = parseGameDateTime(isoString);
    if (!dateTime) {
      return 'Invalid Date';
    }

    const timeZone = getUserTimeZone(user);

    // Format as MM/DD/YY HH:MM AM/PM
    const formatted = dateTime.toLocaleString('en-US', {
      timeZone: timeZone,
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return formatted;
  } catch (error) {
    console.error('Error formatting date time:', error, { isoString });
    return 'Invalid Date';
  }
};

// Convert user timezone datetime-local input to UTC ISO string for database storage
export const convertUserTimeToUTC = (datetimeLocalValue, user = null) => {
  try {
    if (!datetimeLocalValue) return null;

    // datetime-local input is in user's timezone, create Date object
    const userDate = new Date(datetimeLocalValue);

    // Convert to UTC ISO string for database storage
    return userDate.toISOString();
  } catch (error) {
    console.error('Error converting user time to UTC:', error, { datetimeLocalValue });
    return null;
  }
};

// Convert UTC ISO string to user timezone datetime-local format for input fields
export const convertUTCToUserTime = (utcIsoString, user = null) => {
  try {
    if (!utcIsoString) return '';

    const utcDate = new Date(utcIsoString);
    const timeZone = getUserTimeZone(user);

    // Convert to user timezone and format for datetime-local input
    const userDate = new Date(utcDate.toLocaleString('en-US', { timeZone }));
    const offset = userDate.getTimezoneOffset() * 60000;
    const localDate = new Date(userDate.getTime() - offset);

    return localDate.toISOString().slice(0, 16);
  } catch (error) {
    console.error('Error converting UTC to user time:', error, { utcIsoString });
    return '';
  }
};
